// client-logger.js
const isBrowser = typeof window !== 'undefined';

type OutputMode = 'console' | 'file' | 'both';

type TranscriptEvent = {
  type?: string;
  item?: {
    role?: string;
    content?: Array<{
      text?: string;
      transcript?: string;
      message?: string;
    }>;
  };
  transcript?: string;
  delta?: string;
  part?: { transcript?: string; text?: string };
};

// Example usage:
// logRelay('Relaying event', { type: 'response.output_audio.delta', delta: '...' });
// Debug flag
const RELAY_DEBUG = false; // Set to true to enable logging

// Output setting: "console", "file", "both"
const OUTPUT_ENV = (!isBrowser && typeof process !== 'undefined'
  ? (process.env?.RELAY_LOG_OUTPUT as OutputMode | undefined)
  : undefined);
const OUTPUT: OutputMode =
  OUTPUT_ENV === 'file' || OUTPUT_ENV === 'both' || OUTPUT_ENV === 'console'
    ? OUTPUT_ENV
    : 'console';

// Character limit for each logged message (applies to the Binary delta logs)
const MESSAGE_LIMIT = 260;

// Optional filter: only log transcript events when true
const TRANSCRIPT_ONLY = false;
const TRANSCRIPT_EVENTS = new Set([
  'conversation.item.create',
  'conversation.item.created',
  'conversation.item.added',
  'conversation.item.done',
  'conversation.item.input_audio_transcription.completed',
  'response.output_item.done',
  'response.output_audio_transcript.delta',
  'response.content_part.added',
]);
const SIMPLE_TRANSCRIPT_KEYWORDS = [
  'User item.created text resolved',
  'User item.done text resolved',
  'Assistant text resolved',
  'User message cached to memory',
  'Transcribed user message cached',
  'Assistant message cached to memory',
  'localHistory',
  'conversation.item.create',
  'conversation.item.created',
  'conversation.item.added',
  'conversation.item.done',
  'input_audio_transcription.completed',
  '"transcript":"',
  '"text":"',
  'SCB', // force logging for system control block traces
];

// Ensure ./logs folder exists (server-side only)
let fs: typeof import('fs') | null = null;
let path: typeof import('path') | null = null;
let logsDir: string = '';

if (!isBrowser) {
  // Avoid bundler issues in the browser by requiring at runtime
  fs = eval('require')('fs');
  path = eval('require')('path');

  if (fs && path) {
    logsDir = path.resolve(process.cwd(), 'logs');
    if (!fs.existsSync(logsDir)) {
      fs.mkdirSync(logsDir, { recursive: true });
    }
  }
}

if (typeof globalThis.devicePixelRatio === 'undefined') {
  globalThis.devicePixelRatio = 1;
}
// Generate a logfile name with startup timestamp
let logfilePath: string | null = null;

if (!isBrowser && (OUTPUT === 'file' || OUTPUT === 'both') && fs && path) {
  const startupTimestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-') // safe filename
    .replace('T', '_')
    .split('Z')[0];
  const logfileName = `logfile_${startupTimestamp}.txt`;
  logfilePath = path.join(logsDir, logfileName);
  fs.writeFileSync(logfilePath, '', { flag: 'a' }); // ensure file exists
}

// Stringify arbitrary values safely
function toStringSafe(v: unknown): string {
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    try {
      return String(v);
    } catch {
      return '[Unserializable]';
    }
  }
}

// Truncate to MESSAGE_LIMIT characters if needed
function truncate(body: unknown): string {
  const normalized = typeof body === 'string' ? body : toStringSafe(body);
  if (MESSAGE_LIMIT <= 0) return normalized;
  return normalized.length > MESSAGE_LIMIT
    ? normalized.slice(0, MESSAGE_LIMIT) + '…'
    : normalized;
}

/**
 * Safe logger for relay.js
 * - Adds ISO timestamp to each log entry
 * - Truncates message body to MESSAGE_LIMIT characters
 * - Writes according to OUTPUT setting ("console", "file", "both")
 */
export function logRelay(...args: unknown[]): void {
  if (!RELAY_DEBUG) return; // Skip logging when disabled
  try {
    const timestamp = new Date()
      .toISOString()
      .replace('T', ' ')
      .replace('Z', '');

    let body: string;
    if (TRANSCRIPT_ONLY) {
      const transcript = extractTranscriptSnippet(args);
      if (transcript) {
        const snippetBody = shouldApplyBinaryLimit(transcript)
          ? truncate(transcript)
          : transcript;
        const timedSnippet = `[${timestamp}] ${snippetBody}`;
        logOutput(timedSnippet);
        return;
      }

      const joined = args.map(toStringSafe).join(' ');
      const keywordHit = SIMPLE_TRANSCRIPT_KEYWORDS.some((kw) =>
        joined.includes(kw)
      );
      if (!keywordHit) return;
      const joinedBody = shouldApplyBinaryLimit(joined)
        ? truncate(joined)
        : joined;
      const timedJoined = `[${timestamp}] ${joinedBody}`;
      logOutput(timedJoined);
      return;
    }

    body = args.map(toStringSafe).join(' ');
    if (body.includes('response.output_audio.delta')) {
      body = truncate(body);
    }

    const message = `[${timestamp}] ${body}`;
    logOutput(message);
  } catch {
    // Never throw on logging failure
  }
}

function logOutput(message: string): void {
  if (OUTPUT === 'console' || OUTPUT === 'both' || isBrowser) {
    console.log('[RelayDebug logger]', message);
  }
  if (!isBrowser && (OUTPUT === 'file' || OUTPUT === 'both') && logfilePath && fs) {
    fs.appendFileSync(logfilePath, message + '\n');
  }
}

function extractTranscriptSnippet(args: unknown[]): string | null {
  for (const arg of args) {
    const snippet = normalizeTranscriptArg(arg);
    if (snippet) return snippet;
  }
  return null;
}

function normalizeTranscriptArg(arg: unknown): string | null {
  if (arg == null) return null;

  if (typeof arg === 'string') {
    const rawMatch = extractFromRawString(arg);
    if (rawMatch) return rawMatch;

    try {
      const parsed = JSON.parse(arg);
      const match = buildTranscriptLine(parsed);
      if (match) return match;
    } catch {
      // ignore parse errors
    }
    return null;
  }

  if (typeof arg === 'object') {
    const match = buildTranscriptLine(arg as TranscriptEvent);
    if (match) return match;
  }

  return null;
}

function buildTranscriptLine(event: TranscriptEvent): string | null {
  if (!event || typeof event !== 'object') return null;
  if (event.type && !TRANSCRIPT_EVENTS.has(event.type)) return null;

  if (event.type === 'conversation.item.create') {
    const text =
      event.item?.content?.[0]?.text ??
      event.item?.content?.[0]?.transcript ??
      event.item?.content?.[0]?.message;
    if (text) return `[User ➜] ${text}`;
  }

  if ((event.type === 'conversation.item.created' || event.type === 'conversation.item.done') && event.item?.role === 'user') {
    const text =
      event.item?.content?.[0]?.transcript ||
      event.item?.content?.[0]?.text ||
      '';
    if (text) return `[User ✅] ${text}`;
  }

  if (
    event.type === 'conversation.item.input_audio_transcription.completed' &&
    event.transcript
  ) {
    return `[User 🎙] ${event.transcript}`;
  }

  if (
    event.type === 'response.output_item.done' &&
    event.item?.role === 'assistant'
  ) {
    const text =
      event.item?.content?.[0]?.transcript ||
      event.item?.content?.[0]?.text ||
      '';
    if (text) return `[Assistant ✅] ${text}`;
  }

  if (event.type === 'response.output_audio_transcript.delta' && event.delta) {
    return `[Assistant Δ] ${event.delta}`;
  }

  if (event.type === 'response.content_part.added') {
    const part = event.part || {};
    const transcript = part.transcript || part.text || '';
    if (transcript) return `[Transcript] ${transcript}`;
  }

  return null;
}

function extractFromRawString(str: string): string | null {
  const convoMatch = str.match(
    /"type":"conversation\.item\.create"[\s\S]*?"text":"([^"]+)"/
  );
  if (convoMatch) return `[User ➜] ${convoMatch[1]}`;

  const userCreatedMatch = str.match(
    /"type":"conversation\.item\.created"[\s\S]*?"role":"user"[\s\S]*?"transcript":"([^"]*)"/
  );
  if (userCreatedMatch) return `[User ✅] ${userCreatedMatch[1]}`;

  const transcriptMatch = str.match(/"transcript":"([^"]+)"/);
  if (transcriptMatch) return `[Transcript] ${transcriptMatch[1]}`;

  const deltaMatch = str.match(/"delta":"([^"]+)"/);
  if (deltaMatch) return `[Assistant Δ] ${deltaMatch[1]}`;

  return null;
}

function shouldApplyBinaryLimit(content: unknown): boolean {
  if (!TRANSCRIPT_ONLY) return false;
  if (typeof content !== 'string') return false;
  return content.startsWith('[Assistant Δ]');
}
